'use client'

import { useRef, useState } from 'react'

import { Star } from 'lucide-react'

import type { SimplifiedReview } from '@/lib/c2c/types'
import { Link } from '@/i18n/navigation'

const INITIAL_REVIEWS = 5

interface CarrierReviewsProps {
    title?: string
    subtitle?: string
    averageRating?: number
    reviews: SimplifiedReview[]
}

function formatDate(dateStr: string): string {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) {
        return ''
    }
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    })
}

function StarRating({ rating }: { rating: number }) {
    return (
        <span className="mr-2 flex">
            {Array.from({ length: 5 }).map((_, i) => (
                <Star
                    key={i}
                    className={`h-5 w-5 ${
                        i < rating ? 'fill-[#e9c530] text-[#e9c530]' : 'text-gray-300'
                    }`}
                />
            ))}
        </span>
    )
}

function ReviewCard({
    review,
    isFirst,
    isLast,
}: {
    review: SimplifiedReview
    isFirst: boolean
    isLast: boolean
}) {
    const dateStr = formatDate(review.departDate)
    const travelerSuffix = review.travelerType ? ` - ${review.travelerType}` : ''

    return (
        <div
            className={[
                'border-x border-t border-[#d9dee5] px-4 py-4 pb-6 md:p-6',
                isFirst && 'rounded-t-[7px]',
                isLast && 'rounded-b-[7px] border-b',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            {review.rating !== null && (
                <div className="flex items-center pb-4">
                    <StarRating rating={review.rating} />
                    <p className="text-base text-[#44474a]">{review.rating.toFixed(1)}</p>
                </div>
            )}
            <div className="flex flex-col md:flex-row">
                {review.reviewerName && (
                    <span className="text-base font-bold text-[#44474a]">
                        {review.reviewerName}
                    </span>
                )}
                {dateStr && (
                    <p className="text-base text-[#44474a] md:mx-2">
                        {dateStr}
                        {travelerSuffix}
                    </p>
                )}
                {review.carrierName &&
                    (review.carrierGuideSlug ? (
                        <Link
                            href={`/carrier/${review.carrierGuideSlug}/`}
                            className="text-base font-semibold text-[#00bfd5] hover:text-[#008a99] hover:underline"
                        >
                            {review.carrierName}
                        </Link>
                    ) : (
                        <span className="text-base text-[#44474a]">{review.carrierName}</span>
                    ))}
            </div>
            {review.text && (
                <div className="mt-6">
                    <div className="text-base leading-6 text-[#44474a]">{review.text}</div>
                </div>
            )}
        </div>
    )
}

/**
 * Displays carrier reviews for a C2C route.
 * Returns null when no reviews are available.
 * Shows first 5 reviews with a "Show More Reviews" button to reveal the rest.
 */
export function CarrierReviews({
    title = 'Customer Reviews',
    subtitle,
    averageRating,
    reviews,
}: CarrierReviewsProps) {
    const [expanded, setExpanded] = useState(false)
    const buttonRef = useRef<HTMLButtonElement>(null)

    if (reviews.length === 0) {
        return null
    }

    const visibleReviews = expanded ? reviews : reviews.slice(0, INITIAL_REVIEWS)
    const hasMore = reviews.length > INITIAL_REVIEWS

    return (
        <section data-section="reviews" className="my-[72px] md:my-24">
            <h3 className="pb-4 text-2xl font-bold text-[#44474a]">{title}</h3>

            {subtitle && <p className="text-base text-[#44474a]">{subtitle}</p>}

            {averageRating != null && (
                <div className="flex items-center py-4 pb-8">
                    <span className="mr-2 flex">
                        <Star className="h-5 w-5 fill-[#e9c530] text-[#e9c530]" />
                    </span>
                    <span className="text-2xl font-bold text-[#44474a]">
                        {averageRating.toFixed(1)}
                    </span>
                </div>
            )}

            <div>
                {visibleReviews.map((review, index) => (
                    <ReviewCard
                        key={review.id}
                        review={review}
                        isFirst={index === 0}
                        isLast={index === visibleReviews.length - 1}
                    />
                ))}
            </div>

            {hasMore && (
                <div className="pt-8">
                    <button
                        ref={buttonRef}
                        onClick={() => {
                            if (expanded) {
                                setExpanded(false)
                                requestAnimationFrame(() => {
                                    buttonRef.current?.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'end',
                                    })
                                })
                            } else {
                                setExpanded(true)
                            }
                        }}
                        className="rounded-[7px] bg-[#ff5c5c] px-5 py-[15px] text-base font-bold tracking-wide text-white uppercase hover:bg-[#e04545]"
                    >
                        {expanded ? 'Hide Reviews' : 'Show More Reviews'}
                    </button>
                </div>
            )}
        </section>
    )
}
